import { LocalizationProvider } from '@lt/localization';
import React, { Suspense, lazy, useEffect, useState } from 'react';

import './styles/index.scss';
import '../../global.css';

import { useIsClient } from 'usehooks-ts';

import { MODULE_NAME } from 'src/constants';

import { useInView } from 'react-intersection-observer';
import Auth from 'src/routes/Mobile';
import { isSSR } from 'src/helpers/ssr';
import { useForceRender } from 'src/hooks/useForceRender';
import Footer from '../Footer';
import HeaderSkeleton from '../Header/components/HeaderSkeleton';

import HeaderBanner from '../Banners/HeaderBanner';
import FooterAppBanners from './components/FooterAppBanners';

import type { PropsFromRedux } from '.';
import HeaderWriteUs from '../Header/components/HeaderMenu/components/HeaderWriteUs';
import SideNavMenu from '../Header/components/HeaderMenu/components/SideNavMenu';
import WishlistOnboarding from '../WishlistOnboarding';
import { useSidebarOpener } from './hooks/useSidebarOpener';
import type { LayoutOwnProps } from './types';
import BannerController from '../Banners';

const Header = lazy(
  () => import(/* webpackChunkName: "Header" */ 'src/components/Header'),
);

type LayoutProps = PropsFromRedux & LayoutOwnProps;
/**
 * Layout содержит дефолтные стили и базовую разметку для страницы
 * так же Header, который отображаются на каждой странице
 * так же MobileAppBanner, который рендерится в футере
 * так же TopMobileAppBanner, который должен лежать вверху всего на главной странице
 *
 * Далее костыль пока выпиливаем immutable
 * Инициализируем приложение в сторе с помощью действия initAppAction и передаем
 * в него параметры из пропсов: appName - имя страницы, rest данные прокинутые с бэка,
 * либо дополнительные пропсы нужные для конкретной страницы
 */
const Layout = ({
  initApp,
  appName,
  isMobile = false,
  children,
  onShowAuthPopup,
  onLogOut,
  onLoad,
  onHideHeaderBanner,
  isWhiteLabel,
  isLoggedIn,
  showAuthFromWishlist,
  ...rest
}: LayoutProps) => {
  /* force rerender on client, issue: https://github.com/vercel/next.js/issues/7417#issuecomment-509411508 */
  const isClient = useIsClient();
  const { countForceRender } = useForceRender();
  const { isSidebarOpened, openSidebar, closeSidebar } = useSidebarOpener();
  const [isShiftedWishlistOnboarding, setIsShiftedWishlistOnboarding] =
    useState(false);
  const { ref, inView } = useInView();
  useEffect(() => {
    setIsShiftedWishlistOnboarding(inView);
    onHideHeaderBanner(!inView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);
  const [authExpanded, setAuthExpanded] = useState(false);

  const isExplore = appName === MODULE_NAME.SEO;
  const isSearch = appName === MODULE_NAME.SEARCH;
  const isHotel = appName === MODULE_NAME.HOTEL;
  const isCookieBannerDisabled = appName === MODULE_NAME.SBP;
  useEffect(() => {
    onLoad();
  }, [isLoggedIn, onLoad]);

  useEffect(() => {
    if (isClient) {
      initApp({ appName, isMobile, ...rest });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appName, initApp, isMobile, isClient]);

  /* стейт showAuthFromWishlist нужен, чтообы при вызове авторизации из избранного
  отображалась только одна форма авторизации
*/
  useEffect(() => {
    if (showAuthFromWishlist) {
      onShowAuthPopup();
      setAuthExpanded(true);
    }
  }, [showAuthFromWishlist, onShowAuthPopup]);

  return (
    <LocalizationProvider>
      {isClient ? (
        <Suspense
          fallback={
            <>
              <HeaderBanner />
              <HeaderSkeleton isPreview={!isExplore && !isSearch} />
            </>
          }
        >
          <HeaderBanner viewRef={ref} />
          <BannerController isCookieBannerDisabled={isCookieBannerDisabled} />

          {/* Скрываем бургер для Альфы */}
          {(isSSR || window.PARTNER?.id !== 1220) && (
            <WishlistOnboarding
              openSidebar={openSidebar}
              isShiftedWishlistOnboarding={isShiftedWishlistOnboarding}
            />
          )}
          <Header openSidebar={openSidebar} />
          {isSidebarOpened && (
            <Suspense fallback={<div />}>
              <SideNavMenu
                onShowAuthPopup={() => {
                  onShowAuthPopup();
                  setAuthExpanded(true);
                }}
                onLogOut={onLogOut}
                closeSidebar={closeSidebar}
                isWhitelabel={isWhiteLabel}
                isLoggedIn={isLoggedIn}
              />
            </Suspense>
          )}
          {authExpanded && (
            <Suspense fallback={<div />}>
              <Auth />
            </Suspense>
          )}
          <HeaderWriteUs />
        </Suspense>
      ) : (
        <>
          <HeaderBanner />
          <HeaderSkeleton isPreview={!isExplore && !isSearch} />
        </>
      )}
      {children}

      {!isSSR && <FooterAppBanners />}
      {!isWhiteLabel && !(isExplore || isHotel) && (
        <Footer key={countForceRender} />
      )}
    </LocalizationProvider>
  );
};

export default Layout;
